import * as z from "zod"
import { xmlInt } from "../../util/markdown-xml-attributes"
import { objectId } from "../../util/object-id"

export const LINK_SOURCES = ["upload", "google", "dropbox", "onedrive", "other", "link"] as const
export type AttachmentSource = (typeof LINK_SOURCES)[number]

export const LINK_RENDERINGS = ["hyperlink", "card", "embed", "block", "preview", "chip"] as const

/** linkInline's allowed renderAs values; the first is the default. */
export const INLINE_RENDER_AS = ["hyperlink", "chip"] as const
/** linkBlock's allowed renderAs values; the first is the default. */
export const BLOCK_RENDER_AS = ["block", "preview"] as const
// Each node type allows only its own subset — an illegal pair like linkInline +
// "preview" must not compile, parse, or store (linkAttributes clamps it).
export type LinkRenderAs = (typeof INLINE_RENDER_AS)[number] | (typeof BLOCK_RENDER_AS)[number]

// Declare the command here so that it reaches importers of either this
// file or the actual extensions.
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    linkInline: {
      /**
       * Switch the selected link token's form (docs/unified-links.md §6). Same-node
       * forms flip `renderAs` in place; inline ⇄ block conversions replace the node
       * with the other type, spreading the same attrs — lossless either way.
       */
      setLinkRenderAs: (renderAs: LinkRenderAs) => ReturnType
    }
  }
}

const linkFieldShape = {
  attachmentId: z.string().nullable(),
  title: z.string().nullable(),
  type: z.string(),
  mimetype: z.string().nullable(),
  size: z.number().nullable(),
  isWriteable: z.boolean(),
  icon: z.string().nullable(),
  source: z.enum(LINK_SOURCES),
  renderAs: z.enum(LINK_RENDERINGS),
  previewTitle: z.string().nullable(),
  previewDescription: z.string().nullable(),
  previewImageUrl: z.string().nullable(),
  previewFaviconUrl: z.string().nullable(),
}

export const LinkAttrsSchema = z.object({
  url: z.string(),
  attachmentId: linkFieldShape.attachmentId.default(null),
  title: linkFieldShape.title.default(null),
  type: linkFieldShape.type.default("link"),
  mimetype: linkFieldShape.mimetype.default(null),
  size: linkFieldShape.size.default(null),
  isWriteable: linkFieldShape.isWriteable.default(false),
  icon: linkFieldShape.icon.default(null),
  source: linkFieldShape.source.default("link"),
  renderAs: linkFieldShape.renderAs.default("hyperlink"),
  previewTitle: linkFieldShape.previewTitle.default(null),
  previewDescription: linkFieldShape.previewDescription.default(null),
  previewImageUrl: linkFieldShape.previewImageUrl.default(null),
  previewFaviconUrl: linkFieldShape.previewFaviconUrl.default(null),
})

export type LinkAttrs = z.infer<typeof LinkAttrsSchema>

// Attributes of a Markdown `<a>` tag. XML attributes are strings, so non-string fields parse
// from their string form. A field that doesn't parse is dropped rather than failing the link.
export const LinkMarkdownAttrsSchema = z.object({
  href: z.string(),
  attachmentId: z.string().optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  mimetype: z.string().optional().catch(undefined),
  size: xmlInt().optional().catch(undefined),
  isWriteable: z.stringbool().optional().catch(undefined),
  icon: z.string().optional().catch(undefined),
  source: linkFieldShape.source.optional().catch(undefined),
  renderAs: linkFieldShape.renderAs.optional().catch(undefined),
  previewTitle: z.string().optional().catch(undefined),
  previewDescription: z.string().optional().catch(undefined),
  previewImageUrl: z.string().optional().catch(undefined),
  previewFaviconUrl: z.string().optional().catch(undefined),
})

export type Link = Omit<LinkAttrs, "title" | "renderAs"> & {
  title: string
  renderAs: LinkRenderAs
}

export interface IdentifiedLink extends Link {
  attachmentId: string
}

export function linkAttrsFromUrl<T extends string | null = null>(
  url: string,
  title: string,
  attachmentId?: T,
): T extends string ? IdentifiedLink : Link {
  return {
    attachmentId: attachmentId ?? null,
    url,
    title,
    type: "link",
    mimetype: null,
    size: null,
    isWriteable: false,
    icon: null,
    source: "link",
    renderAs: "hyperlink",
    previewTitle: null,
    previewDescription: null,
    previewImageUrl: null,
    previewFaviconUrl: null,
  } as any
}

export function linkFromUrl(url: string, title: string): IdentifiedLink {
  return linkAttrsFromUrl(url, title, objectId())
}
